import {
  activatePerformanceMeasureDefinition,
  deactivatePerformanceMeasureDefinition,
  type QuestionnaireDefinitionDto,
  usePerformanceMeasureDefinitions,
} from "@repo/data-store";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { renderQuestionnaireCells } from "@/features/catalog";
import { questionnaireDefinitionRouteId } from "@/features/catalog/lib/questionnaire-definition-route";
import { ActionButton } from "@/shared/common/action-button";
import { GroupedList } from "@/shared/common/grouped-list";
import { SearchFilterBar } from "@/shared/common/search-filter-bar";

export const Route = createFileRoute("/catalog/_layout/performance-measures")({
  component: PerformanceMeasures,
  validateSearch: (search: Record<string, unknown>): { q?: string; status?: string } => ({
    q: typeof search.q === "string" ? search.q : undefined,
    status: typeof search.status === "string" ? search.status : undefined,
  }),
});
function PerformanceMeasures() {
  const { q = "", status } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { permissions } = Route.useRouteContext();
  const canWrite = permissions.canWrite("Questionnaire");
  const measures = usePerformanceMeasureDefinitions({
    search: q,
    availability: status ? (status === "active" ? "Active" : "Inactive") : undefined,
  });
  const changeStatus = async (definition: QuestionnaireDefinitionDto, status: string) => {
    try {
      await (status === "active" ? activatePerformanceMeasureDefinition : deactivatePerformanceMeasureDefinition)({
        definitionUrl: definition.url,
      }).isPersisted.promise;
    } catch {
      /* Shared outbox reports failures. */
    }
  };
  return (
    <GroupedList
      groups={[
        {
          id: "performance-measures",
          name: "Performance measures",
          items: measures,
          headerAction: () => (
            <ActionButton
              state={{ allowed: canWrite }}
              variant="ghost"
              size="icon-xs"
              to="/catalog/performance-measures/new"
            >
              <Plus className="size-4" />
            </ActionButton>
          ),
        },
      ]}
      isLoading={false}
      header={
        <SearchFilterBar
          search={q}
          onSearchChange={(q) => navigate({ search: (prev) => ({ ...prev, q: q || undefined }), replace: true })}
          placeholder="Search performance measures..."
          statusOptions={[
            { value: "active", label: "Active" },
            { value: "retired", label: "Inactive" },
          ]}
          statusValue={status ?? null}
          onStatusChange={(status) =>
            navigate({ search: (prev) => ({ ...prev, status: status || undefined }), replace: true })
          }
        />
      }
      columns={[
        { id: "title", width: "1fr" },
        { id: "date", width: "auto" },
        { id: "status", width: "100px" },
      ]}
      renderCells={(definition) => renderQuestionnaireCells(definition, canWrite ? changeStatus : undefined)}
      wrapItem={(definition, children) => (
        <Link
          to="/catalog/performance-measures/$questionnaireId"
          params={{ questionnaireId: questionnaireDefinitionRouteId(definition.url) }}
          className="cursor-pointer"
        >
          {children}
        </Link>
      )}
      renderItem={() => null}
      getItemKey={(definition) => definition.url}
      emptyMessage="No performance measures found"
      isFiltered={!!q || !!status}
    />
  );
}
